import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import * as kb from "@models";
import { IEntity, IRootScope, meta } from "@models";
import { Utils } from "@tools";

export interface IProxyClient<TModel extends IEntity> {
    getFromEnvironment(id: number, env: string, tracker?: ng.IPromiseTracker): ng.IPromise<TModel>;
    rebuildIndex(): ng.IPromise<any>;
}

@NgService({
    token: Token.ApiService,
    dependencies: [
        Token.$Http,
        Token.$Q,
        Token.$RootScope,
        Token.$Location
    ]
})
export class ApiService {

    constructor(
        private $http: ng.IHttpService,
        private $q: ng.IQService,
        private $rootScope: IRootScope,
        private $location: ng.ILocationService
    ) {
        let queryString = $location.search();
        let cookieToken = queryString["cookieToken"];
        if (cookieToken) {
            $http.defaults.headers.common["x-embed-token"] = cookieToken;
        }
    }

    // public injection(): any[] {
    //     return [
    //         Token.$Http,
    //         Token.$Q,
    //         ApiService
    //     ];
    // }

    public adminProducts: AdminProductClient =
        new AdminProductClient(this.$http, this.$q, "/api/admin/products");
    public assistantFeedback: CrudClient<kb.ISnapAssistantFeedback> =
        new CrudClient<kb.ISnapAssistantFeedback>(this.$http, this.$q, "/api/assistant/feedback");
    public assistant: AssistantClient =
        new AssistantClient(this.$http, this.$q, "/api/assistant");
    public automationStudio: AutomationStudioClient =
        new AutomationStudioClient(this.$http, this.$q, "/api/automationstudio");
    public builders: CrudClient<kb.IOutputBuilder> =
        new CrudClient<kb.IOutputBuilder>(this.$http, this.$q, "/api/builders");
    public buildTypes: CrudClient<kb.IBuildType> =
        new CrudClient<kb.IBuildType>(this.$http, this.$q, "/api/buildtypes");
    public builderSettings: SingletonCrudClient<kb.IOutputBuilderSetting> =
        new SingletonCrudClient<kb.IOutputBuilderSetting>(this.$http, this.$q, "/api/buildersettings");
    public categories: CategoriesClient =
        new CategoriesClient(this.$http, this.$q, "/api/categories");
    public changePassword: CrudClient<kb.IPasswordChange> =
        new CrudClient<kb.IPasswordChange>(this.$http, this.$q, "/api/users/changepassword");
    public channels: CrudClient<kb.IChannel> =
        new CrudClient<kb.IChannel>(this.$http, this.$q, "/api/channels");
    public companies: CrudClient<kb.ICompany> =
        new CrudClient<kb.ICompany>(this.$http, this.$q, "/api/companies");
    public configurators: ConfiguratorClient =
        new ConfiguratorClient(this.$http, this.$q, "/api/admin/products");
    public contacts: CrudClient<kb.IContact> =
        new CrudClient<kb.IContact>(this.$http, this.$q, "/api/contacts");
    public currencies: CurrencyClient =
        new CurrencyClient(this.$http, this.$q, "/api/currencies");
    public customActions: CrudClient<kb.ICustomAction> =
        new CrudClient<kb.ICustomAction>(this.$http, this.$q, "/api/customactions");
    public customers: CrudClient<kb.ICustomer> = new CrudClient<kb.ICustomer>(this.$http, this.$q, "/api/customers");
    public dashboard: SingletonCrudClient<kb.IAdminReport> =
        new SingletonCrudClient(this.$http, this.$q, "/api/dashboard");
    public dbLogins: DbLoginsClient =
        new DbLoginsClient(this.$http, this.$q, "/api/dblogins");
    public dbTables: DbTablesClient =
        new DbTablesClient(this.$http, this.$q, "/api/dbtables");
    public dependencies: DependenciesClient =
        new DependenciesClient(this.$http, this.$q, "/api/dependencies");
    public deployments: DeploymentClient =
        new DeploymentClient(this.$http, this.$q, "/api/deployments");
    public emailTemplates: CrudClient<kb.IEmailTemplate> =
        new CrudClient<kb.IEmailTemplate>(this.$http, this.$q, "/api/emailtemplates");
    public functions: CrudClient<kb.ISafeFunction> =
        new CrudClient<kb.ISafeFunction>(this.$http, this.$q, "/api/functions");
    public generators: GeneratorClient =
        new GeneratorClient(this.$http, this.$q, "/api/generators");
    public globalDefinitions: CrudClient<kb.IGlobalDefinition> =
        new CrudClient<kb.IGlobalDefinition>(this.$http, this.$q, "/api/globaldefinitions");
    public globalFunctions: GlobalFunctionClient =
        new GlobalFunctionClient(this.$http, this.$q, "/api/globalfunctions");
    public integrationSettings: IntegrationSettingsClient =
        new IntegrationSettingsClient(this.$http, this.$q, "/api/integrationsettings");
    public jobs: JobsClient =
        new JobsClient(this.$http, this.$q, "/api/jobs");
    public kinetic: KineticClient =
        new KineticClient(this.$http, this.$q, "/api/kinetic");
    public languages: LanguageClient =
        new LanguageClient(this.$http, this.$q, "/api/languages");
    public layouts: LayoutClient =
        new LayoutClient(this.$http, this.$q, "/api/layouts");
    public logs: CrudClient<kb.ILog> =
        new CrudClient<kb.ILog>(this.$http, this.$q, "/api/logs");
    public notifications: NotificationClient =
        new NotificationClient(this.$http, this.$q, "/api/notifications");
    public notificationSettings: SingletonCrudClient<kb.INotificationSetting> =
        new SingletonCrudClient<kb.INotificationSetting>(this.$http, this.$q, "/api/notificationsettings");
    public optionFilters: OptionFilterClient =
        new OptionFilterClient(this.$http, this.$q, "/api/optionfilters");
    public outputBuilders: CrudClient<kb.IOutputBuilder> =
        new CrudClient<kb.IOutputBuilder>(this.$http, this.$q, "/api/builders");
    public permissions: PermissionClient =
        new PermissionClient(this.$http, this.$q, "/api/permissions");
    public priceColumns: PriceColumnClient =
        new PriceColumnClient(this.$http, this.$q, "/api/pricecolumns");
    public products: ProductClient =
        new ProductClient(this.$http, this.$q, "/api/products");
    public productHistory: ProductHistoryClient =
        new ProductHistoryClient(this.$http, this.$q, "/api/producthistory");
    public productRules: SingletonCrudClient<kb.IProductRule> =
        new SingletonCrudClient<kb.IProductRule>(this.$http, this.$q, "/api/productrules");
    public queues: CrudClient<kb.IQueue> =
        new CrudClient<kb.IQueue>(this.$http, this.$q, "/api/queues");
    public quoteHeaders: QuoteHeaderClient =
        new QuoteHeaderClient(this.$http, this.$q, "/api/quoteheaders");
    public quoteOutputs: CrudClient<kb.IQuoteOutput> =
        new CrudClient<kb.IQuoteOutput>(this.$http, this.$q, "/api/quoteoutputs");
    public quotes: QuoteClient =
        new QuoteClient(this.$http, this.$q, "/api/quotes");
    public quoteProducts: CrudClient<kb.IQuoteProduct> =
        new CrudClient<kb.IQuoteProduct>(this.$http, this.$q, "/api/quoteproducts");
    public roles: CrudClient<kb.IKbRole> =
        new CrudClient<kb.IKbRole>(this.$http, this.$q, "/api/roles");
    public rootUsers: RootUserClient =
        new RootUserClient(this.$http, this.$q, "/api/rootusers");
    public safeFunctions: SafeFunctionClient =
        new SafeFunctionClient(this.$http, this.$q, "/api/functions");
    public savedSearches: SavedSearchClient =
        new SavedSearchClient(this.$http, this.$q, "/api/savedsearches");
    public scenes: ScenesClient =
        new ScenesClient(this.$http, this.$q, this.$rootScope, "/api/scenes");
    public sceneHistory: SceneHistoryClient = 
        new SceneHistoryClient(this.$http, this.$q, '/api/scenehistory');
    public settings: CompanySettingsClient =
        new CompanySettingsClient(this.$http, this.$q, "/api/settings");
    public tables: TablesClient =
        new TablesClient(this.$http, this.$q, "/api/tables");
    public tags: CrudClient<kb.ITag> =
        new CrudClient<kb.ITag>(this.$http, this.$q, "/api/tags");
    public testBuilds: TestBuildClient =
        new TestBuildClient(this.$http, this.$q, "/api/testbuilds");
    public themes: CrudClient<kb.ITheme> =
        new CrudClient<kb.ITheme>(this.$http, this.$q, "/api/themes");
    public tools: ToolsClient =
        new ToolsClient(this.$http, this.$q, "api/tools");
    public translate: TranslateClient =
        new TranslateClient(this.$http, this.$q, "api/translate");
    public users: UserClient =
        new UserClient(this.$http, this.$q, "/api/users");
    public workflows: WorkflowsClient =
        new WorkflowsClient(this.$http, this.$q, "/api/workflows");
    public workflowSelectors: SingletonCrudClient<kb.IWorkflowSelector> =
        new SingletonCrudClient<kb.IWorkflowSelector>(this.$http, this.$q, "/api/workflowselector");
    public dbWhitelist: DbWhitelistClient =
        new DbWhitelistClient(this.$http, this.$q, "/api/dbwhitelist");
    public dbXuserCreds: DbXuserCred =
        new DbXuserCred(this.$http, this.$q, "/api/dbxuser");
    public interactions: CrudClient<kb.IInteraction> =
        new CrudClient(this.$http, this.$q, "/api/interactions");
    public kineticMom: KineticMomClient =
        new KineticMomClient(this.$http, this.$q, "/api/mom");
    public materials: CrudClient<kb.IMaterial> =
        new CrudClient<kb.IMaterial>(this.$http, this.$q, "/api/materials");

    public getProxyClient(entityTypeName: string, admin: boolean): IProxyClient<any> {
        let t = entityTypeName.toCamelCase();
        if (t.startsWith("kb")) t = t.substr(2);
        if (t.toLowerCase() == "useredit") t = "user";
        t = t.endsWith("y") ? t.substr(0, t.length - 1) + "ies" : t + "s";
        if (t.startsWith("company")) t = t.substr(7);
        t = t.toCamelCase();
        let service = this[t];

        if (admin) {
            if (service == this.products) service = this.adminProducts;
            if (t.toLowerCase() == "companysettings") service = this.settings;
        }
        return service;
    }
}

export class CrudClient<TModel extends IEntity> implements IProxyClient<TModel> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getById(id: number, tracker?: ng.IPromiseTracker): ng.IPromise<TModel> {
        return this.$http.get<TModel>(`${this.baseUrl}/${id}`, { tracker }).then(r => r.data);
    }

    public getFromEnvironment(id: number, env: string, tracker?: ng.IPromiseTracker): ng.IPromise<TModel> {
        return this.$http.get<TModel>(`${this.baseUrl}/${id}/diff`, { params: { env }, tracker }).then(r => r.data);
    }

    public edit(model: TModel, tracker?: ng.IPromiseTracker, waitForRefresh = false): ng.IPromise<TModel> {
        return this.$http.put<TModel>(`${this.baseUrl}/${model.id}?waitForRefresh=${waitForRefresh}`, model, { tracker }).then(r => r.data);
    }

    public insert(model: TModel, tracker?: ng.IPromiseTracker, waitForRefresh = false): ng.IPromise<TModel> {
        return this.$http.post<TModel>(`${this.baseUrl}?waitForRefresh=${waitForRefresh}`, model, { tracker }).then(r => r.data);
    }

    public upsert(model: TModel, tracker?: ng.IPromiseTracker, waitForRefresh = false): ng.IPromise<TModel> {
        if (model.id) {
            return this.edit(model, tracker, waitForRefresh);
        } else {
            return this.insert(model, tracker, waitForRefresh);
        }
    }

    public delete(id: number, tracker?: ng.IPromiseTracker, ignoreSecurity: boolean = false): ng.IPromise<any> {
        return this.$http.delete(`${this.baseUrl}/${id}`, { tracker, ignoreSecurity });
    }

    public clone(id: number, tracker?: ng.IPromiseTracker): ng.IPromise<TModel> {
        return this.$http.post<TModel>(`${this.baseUrl}/${id}/clone`, null, { tracker }).then(r => r.data);
    }

    public getPaged(p: kb.IPagingRequest, tracker?: ng.IPromiseTracker): ng.IPromise<TModel[]> {
        return this.$http.get<kb.IPagedResult<TModel>>(this.baseUrl, { params: p, tracker }).then(r => r.data.items);
    }

    public searchPaged(p: kb.IPagingRequest, tracker?: ng.IPromiseTracker): ng.IPromise<TModel[]> {
        return this.$http.post<kb.IPagedResult<TModel>>(this.baseUrl + "/searchPaged", p).then(r => r.data.items);
    }

    public getBatch(request: kb.IBatchRequest, tracker?: ng.IPromiseTracker): ng.IPromise<TModel[]> {
        return this.$http.get<TModel[]>(`${this.baseUrl}/batch`, { params: request, tracker }).then(r => r.data);
    }

    public search(args?: kb.ISearchArgs, tracker?: ng.IPromiseTracker): ng.IPromise<TModel[]> {
        // fill in args with defaults
        let defaultArgs: kb.ISearchArgs = {
            query: null,
            fields: null,
            sortField: "name",
            descending: false,
            skip: 0,
            take: 10000
        };

        let fullArgs = Utils.extend({}, defaultArgs, args);
        return this.$http.post<TModel[]>(`${this.baseUrl}/search`, fullArgs, { tracker }).then(r => r.data);
    }

    public suggest(args: kb.ISuggestArgs): ng.IPromise<TModel[]> {
        if (args.query) {
            return this.$http.post<TModel[]>(`${this.baseUrl}/suggest`, args).then(r => r.data);
        } else {
            return this.$q.when([]);
        }
    }

    public uniqueValues(args: kb.IUniqueValuesArgs): ng.IPromise<any[]> {
        return this.$http.post<any[]>(`${this.baseUrl}/uniquevalues`, args).then(r => r.data);
    }

    public rebuildIndex(tracker?: ng.IPromiseTracker): ng.IPromise<any> {
        return this.$http.post<any>(`${this.baseUrl}/rebuildindex`, null, { tracker });
    }
}

export class AutomationStudioClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getToken(tracker?: ng.IPromiseTracker): ng.IPromise<string> {
        return this.$http.get<string>(`${this.baseUrl}/token`, { tracker }).then(r => r.data);
    }
}

export class TranslateClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public translateArray(request: kb.ITranslateArrayRequest, tracker?: ng.IPromiseTracker): ng.IPromise<string[]> {
        return this.$http.post<string[]>(`${this.baseUrl}/array`, request, { tracker }).then(r => r.data);
    }
}

export class DependenciesClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public isRequiredByDependency(type: string, ids: string[] | number[], tracker?: ng.IPromiseTracker): ng.IPromise<boolean> {
        return this.$http.post<boolean>(`${this.baseUrl}/isrequired`, { type, ids }, { tracker }).then(r => r.data);
    }
}

export class DeploymentClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getById(id: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDeployment> {
        return this.$http.get(`${this.baseUrl}/${id}`, { tracker }).then(r => r.data);
    }

    public getPaged(p: kb.IPagingRequest, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDeployment[]> {
        return this.$http.get<kb.IPagedResult<kb.IDeployment>>(this.baseUrl, { params: p, tracker })
            .then(r => r.data.items);
    }

    public deploy(model: kb.IDeployment): ng.IPromise<kb.IDeployment> {
        return this.$http.post<kb.IDeployment>("/api/deploy", model).then(r => r.data);
    }

    public compare(model: kb.IDeployment, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDeployment> {
        return this.$http.post("/api/deploy/compare", model, { tracker }).then(r => r.data);
    }
}

export class AssistantClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getUsageData(tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDeployment> {
        //return this.$http.get(`/api/assistant/usage`, { tracker }).then(r => r.data);
        return this.get("/usage");
    }

    private async get<T>(endpoint: string): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;

        const requestInit: RequestInit = {
            method: 'GET',
            credentials: 'include'
        };

        const response = await fetch(url, requestInit);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return response.json();
    }
}

export class SingletonCrudClient<TModel> implements IProxyClient<TModel> {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public get(tracker?: ng.IPromiseTracker): ng.IPromise<TModel> {
        return this.$http.get<TModel>(`${this.baseUrl}`, { tracker }).then(r => r.data);
    }

    public edit(model: TModel, tracker?: ng.IPromiseTracker): ng.IPromise<TModel> {
        return this.$http.put<TModel>(`${this.baseUrl}`, model, { tracker }).then(r => r.data);
    }

    public getFromEnvironment(id: number, env: string, tracker?: ng.IPromiseTracker): ng.IPromise<TModel> {
        return this.$http.get<TModel>(`${this.baseUrl}/diff`, { params: { env }, tracker }).then(r => r.data);
    }

    public rebuildIndex(tracker?: ng.IPromiseTracker): ng.IPromise<any> {
        return this.$http.post<any>(`${this.baseUrl}/rebuildindex`, null, { tracker });
    }
}

export class PermissionClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public get(tracker?: ng.IPromiseTracker): ng.IPromise<kb.IPermission[]> {
        return this.$http.get<kb.IPermission[]>(`${this.baseUrl}`, { tracker }).then(r => r.data);
    }

    public save(model: kb.IPermission[], tracker?: ng.IPromiseTracker): ng.IPromise<kb.IPermission[]> {
        return this.$http.post<kb.IPermission[]>(`${this.baseUrl}`, model, { tracker }).then(r => r.data);
    }

}

export class ToolsClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public runIndexOperation(args: kb.IAdminIndexOperationArgs, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IAdminIndexOperationResult[]>{
        return this.$http.post<kb.IAdminIndexOperationResult[]>(`/api/search/run`, args, { tracker }).then(r => r.data);
    }

    public rebuildIndex(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`/api/search/rebuild`, null, { tracker });
    }

    public rebuildIndexAllEnv(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`/api/search/rebuildallenv`, null, { tracker });
    }

    public rebuildIndexAll(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`/api/search/rebuildall`, null, { tracker });
    }

    public rebuildRootIndex(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`/api/search/rebuildroot`, null, { tracker });
    }

    public recreateStorage(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`/api/storage/create`, null, { tracker });
    }

    public pullFromProduction(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`${this.baseUrl}/pullfromprod`, null, { tracker });
    }

    public update3DPlugin(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`/api/debug/post-3d-plugin`, null, { tracker });
    }

    public recreate3D(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`/api/recreate3d`, null, { tracker });
    }

    public recreateDns(tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`/api/recreatedns`, null, { tracker });
    }

    public cleanDb(args: kb.ICleanDbQueueMessage, tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post("${this.baseUrl}/cleanDb", args, { tracker });
    }

    public pullSfdcProducts(soqlStatement: string, tracker?: ng.IPromiseTracker): ng.IHttpPromise<any> {
        return this.$http.post(`${this.baseUrl}/pullsfdcproducts`, { whereClause: soqlStatement }, { tracker });
    }

    public getSearchableTypeNames(tracker?: ng.IPromiseTracker): ng.IPromise<string[]> {
        return this.$http.get<string[]>(`/api/search/searchableTypes`, { tracker }).then(r => r.data);
    }
}

export class PriceColumnClient extends CrudClient<kb.IPriceColumn> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public getPriceColumnsForUser(tracker?: ng.IPromiseTracker): ng.IPromise<kb.IPriceColumn[]> {
        return this.$http.get<kb.IPriceColumn[]>(`${this.baseUrl}/user`).then(r => r.data);
    }

    public reorder(args: kb.IPriceColumnReorder[], tracker?: ng.IPromiseTracker): ng.IPromise<kb.IPriceColumn[]> {
        return this.$http.post<kb.IPriceColumn[]>(`${this.baseUrl}/reorder`, args, { tracker }).then(r => r.data);
    }
}

export class KineticClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getDocumentRulesSchema(idConfig: number): ng.IHttpPromise<any> {
        return this.$http.get(`${this.baseUrl}/documentRuleSchema/${idConfig}`, null);
    }

    public getDocumentRulesSchemaForDynamicAttributes(idConfig: number, company: string): ng.IHttpPromise<any> {
        return this.$http.get(`${this.baseUrl}/documentRuleSchema/${idConfig}/${company}`, null);
    }

    public partNumExists(partNum: string): ng.IPromise<boolean> {
        return this.$http.get(`${this.baseUrl}/partNum/${partNum}/exists`).then(r => <boolean>r.data);
    }

    public testConnection(): ng.IHttpPromise<any> {
        return this.$http.get(`${this.baseUrl}/test`, null);
    }

    public getCompanies(): ng.IHttpPromise<kb.IKineticCompany[]> {
        return this.$http.get<kb.IKineticCompany[]>(`${this.baseUrl}/companies`, null);
    }
}

export class JobsClient extends CrudClient<kb.IJob> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public getLogs(idJob: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.ILog[]> {
        return this.$http.get<kb.ILog[]>(`${this.baseUrl}/${idJob}/logs`).then(r => r.data);
    }
}


export class TablesClient extends CrudClient<kb.ITable> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public updateFromSource(id: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.ITable> {
        return this.$http.post(`${this.baseUrl}/${id}/updatefromsource`, null, { tracker }).then(r => r.data);
    }
}

export class WorkflowsClient extends CrudClient<kb.IWorkflow> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public states(tracker?: ng.IPromiseTracker): ng.IPromise<kb.IStateSelection[]> {
        return this.$http.get<kb.IStateSelection[]>(`/api/states`, { tracker }).then(r => r.data);
    }
}

export class ScenesClient extends CrudClient<kb.IScene> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public $rootScope: IRootScope, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public search(args?: kb.ISearchArgs, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProduct[]> {
        args = args || {};
        args.filters = args.filters || [];

        let externalValues = [];
        if (this.$rootScope.company.kb3dEnabled) externalValues.push(false);
        if (this.$rootScope.company.classic3dEnabled) externalValues.push(true);
        args.filters.push({
            property: "external",
            values: externalValues
        });
        
        return super.search(args, tracker);
    }

    public convertToKb3d(id: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IJob> {
        return this.$http.post(`${this.baseUrl}/${id}/convert`, null, { tracker }).then(r => r.data);
    }
}

export class SceneHistoryClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getSceneHistory(idScene: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.ISceneHistory[]> {
        return this.$http.get<kb.ISceneHistory[]>(`${this.baseUrl}/${idScene}`, { tracker }).then(r => r.data);
    }

    public getHistoryByIdHistory(idHistory: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProductHistory> {
        return this.$http.get<kb.IProductHistory>(`${this.baseUrl}/idhist/${idHistory}`, { tracker }).then(r => r.data);
    }

    public cloneHistoryToNewScene(idSceneHistory: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IScene> {
        return this.$http.post<kb.IScene>(`${this.baseUrl}/clone/${idSceneHistory}`, {}, { tracker }).then(r => r.data);
    }

    public revertSceneToHistory(idSceneHistory: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IScene> {
        return this.$http.post<kb.IScene>(`${this.baseUrl}/revert/${idSceneHistory}`, {}, { tracker }).then(r => r.data);
    }
}

export class NotificationClient extends CrudClient<kb.INotification> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }
}

export class CategoriesClient extends CrudClient<kb.ICategory> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public tree(params: {}, tracker?: ng.IPromiseTracker): ng.IPromise<kb.ICategoryNode[]> {
        return this.$http.get<kb.ICategoryNode[]>("api/categories/tree", { params, tracker }).then(r => r.data);
    }
}

export class OptionFilterClient {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public run(args: kb.IOptionFilterRunArgs, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IOptionFilterRunResult> {
        return this.$http.post<kb.IOptionFilterRunResult>(`${this.baseUrl}/run`, args, { tracker }).then(r => r.data);
    }
}

export class DbLoginsClient {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getAll(tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbLogin[]> {
        return this.$http.get<kb.IDbLogin[]>(`${this.baseUrl}`, { tracker }).then(r => r.data);
    }

    public get(name: string, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbLogin> {
        return this.$http.get<kb.IDbLogin>(`${this.baseUrl}/${name}`, { tracker }).then(r => r.data);
    }

    public search(args?: kb.ISearchArgs, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbLogin[]> {
        // fill in args with defaults
        let defaultArgs: kb.ISearchArgs = {
            query: null,
            fields: null,
            sortField: "name",
            descending: false,
            skip: 0,
            take: 10000
        };

        let fullArgs = Utils.extend({}, defaultArgs, args);
        return this.$http.post<kb.IDbLogin[]>(`${this.baseUrl}/search`, fullArgs, { tracker }).then(r => r.data);
    }

    public suggest(args: kb.ISuggestArgs): ng.IPromise<kb.IDbLogin[]> {
        if (args.query) {
            return this.$http.post<kb.IDbLogin[]>(`${this.baseUrl}/suggest`, args).then(r => r.data);
        } else {
            return this.$q.when([]);
        }
    }

    public delete(name: string, tracker?: ng.IPromiseTracker, ignoreSecurity: boolean = false): ng.IPromise<any> {
        return this.$http.delete(`${this.baseUrl}/${name}`, { tracker, ignoreSecurity });
    }
}

export class DbTablesClient {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getAll(tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable[]> {
        return this.$http.get<kb.IDbTable[]>(`${this.baseUrl}`, { tracker }).then(r => r.data);
    }

    public get(name: string, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.get<kb.IDbTable>(`${this.baseUrl}/${name}`, { tracker }).then(r => r.data);
    }

    public getAllSizeInfo(tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTableSizeInfo[]> {
        return this.$http.get<kb.IDbTableSizeInfo[]>(`${this.baseUrl}/sizes`, { tracker }).then(r => r.data);
    }

    public search(args?: kb.ISearchArgs, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable[]> {
        // fill in args with defaults
        let defaultArgs: kb.ISearchArgs = {
            query: null,
            fields: null,
            sortField: "name",
            descending: false,
            skip: 0,
            take: 10000
        };

        let fullArgs = Utils.extend({}, defaultArgs, args);
        return this.$http.post<kb.IDbTable[]>(`${this.baseUrl}/search`, fullArgs, { tracker }).then(r => r.data);
    }

    public suggest(args: kb.ISuggestArgs): ng.IPromise<kb.IDbTable[]> {
        if (args.query) {
            return this.$http.post<kb.IDbTable[]>(`${this.baseUrl}/suggest`, args).then(r => r.data);
        } else {
            return this.$q.when([]);
        }
    }

    public delete(name: string, tracker?: ng.IPromiseTracker, ignoreSecurity: boolean = false): ng.IPromise<any> {
        return this.$http.delete(`${this.baseUrl}/${name}`, { tracker, ignoreSecurity });
    }

    public addColumn(tableName: string, column: kb.ISqlColumnDefinition, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.post<kb.IDbTable>(`${this.baseUrl}/${tableName}/column`, column, { tracker }).then(r => r.data);
    }

    public editColumn(tableName: string, columnName: string, column: kb.ISqlColumnDefinition, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.put<kb.IDbTable>(`${this.baseUrl}/${tableName}/column/${columnName}`, column, { tracker }).then(r => r.data);
    }

    public deleteColumn(tableName: string, columnName: string, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.delete<kb.IDbTable>(`${this.baseUrl}/${tableName}/column/${columnName}`, { tracker }).then(r => r.data);
    }

    public addIndex(tableName: string, index: kb.ISqlIndexDefinition, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.post<kb.IDbTable>(`${this.baseUrl}/${tableName}/index`, index, { tracker }).then(r => r.data);
    }

    public deleteIndex(tableName: string, indexName: string, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.delete<kb.IDbTable>(`${this.baseUrl}/${tableName}/index/${indexName}`, { tracker }).then(r => r.data);
    }

    public addForeignKey(tableName: string, foreignKey: kb.ISqlForeignKeyDefinition, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.post<kb.IDbTable>(`${this.baseUrl}/${tableName}/foreignKey`, foreignKey, { tracker }).then(r => r.data);
    }

    public editForeignKey(tableName: string, foreignKeyName: string, foreignKey: kb.ISqlForeignKeyDefinition, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.put<kb.IDbTable>(`${this.baseUrl}/${tableName}/foreignKey/${foreignKeyName}`, foreignKey, { tracker }).then(r => r.data);
    }

    public deleteForeignKey(tableName: string, foreignKeyName: string, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbTable> {
        return this.$http.delete<kb.IDbTable>(`${this.baseUrl}/${tableName}/foreignKey/${foreignKeyName}`, { tracker }).then(r => r.data);
    }
}

export class IntegrationSettingsClient extends SingletonCrudClient<kb.IIntegrationSetting> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public checkSfdcCredentials(): ng.IPromise<any> {
        return this.$http.get(`/api/integrationsettings/checksfdccredentials`).then(r => r.data);
    }

    public checkOracleCpqCredentials(): ng.IPromise<any> {
        return this.$http.get(`/api/integrationsettings/checkoraclecpqcredentials`).then(r => r.data);
    }

    public getSsoGroups(keywords:string): ng.IPromise<kb.IADGroup[]> {
        return this.$http.get<kb.IADGroup[]>(`/api/integrationsettings/ssogroups?keywords=${keywords}`).then(r => r.data);
    }

    public getSsoGroupById(groupId: string): ng.IPromise<kb.IADGroup> {
        return this.$http.get<kb.IADGroup>(`/api/integrationsettings/ssogroup/${groupId}`).then(r => r.data);
    }
}

export class CurrencyClient extends CrudClient<kb.ICompanyCurrency> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    /**
     * gets all company currencies
     * @param tracker
     */
    public all(tracker?: ng.IPromiseTracker): ng.IPromise<kb.ICompanyCurrency[]> {
        return this.$http.get<kb.ICompanyCurrency[]>(`${this.baseUrl}/all`, { tracker }).then(r => r.data);
    }

    /**
     * gets a list of all currencies (not just company currencies).  
     * Used for populating a select box to add new currencies.
     * @param tracker
     */
    public options(tracker?: ng.IPromiseTracker): ng.IPromise<kb.ICurrency[]> {
        return this.$http.get<kb.ICurrency[]>(`${this.baseUrl}/options`, { tracker }).then(r => r.data);
    }

    /**
     * gets a list of company currencies that also includes the base currency.  
     * Used for populating a select box in the quote screen for example.
     * @param tracker
     */
    public companyOptions(tracker?: ng.IPromiseTracker): ng.IPromise<kb.ICompanyCurrency[]> {
        return this.$http.get<kb.ICompanyCurrency[]>(`${this.baseUrl}/companyoptions`, { tracker }).then(r => r.data);
    }

    /**
     * saves the company currencies.
     * @param companyCurrencies
     * @param tracker
     */
    public save(
        companyCurrencies: kb.ICompanyCurrency[],
        tracker?: ng.IPromiseTracker
    ): ng.IPromise<kb.ICompanyCurrency[]> {
        return this.$http.post<kb.ICompanyCurrency[]>(`${this.baseUrl}/save`, companyCurrencies, { tracker })
            .then(r => r.data);
    }

    public getExchangeRates(baseCurrency?: string, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IExchangeRateGroup> {
        return this.$http.get(`${this.baseUrl}/exchangerates/${baseCurrency || ""}`, { tracker }).then(r => r.data);
    }
}

export class LanguageClient extends CrudClient<kb.ICompanyLanguage> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    /**
     * gets a list of all languages supported by CPQ (not just company languages).  
     * Used for populating a select box to add new languages.
     * @param tracker
     */
    public supportedLanguages(tracker?: ng.IPromiseTracker): ng.IPromise<kb.ILanguage[]> {
        return this.$http.get<kb.ILanguage[]>(`${this.baseUrl}/supported`, { tracker }).then(r => r.data);
    }
        
}

export class GeneratorClient extends CrudClient<kb.IGenerator> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public next(id: number, tracker?: ng.IPromiseTracker): ng.IPromise<number> {
        return this.$http.get<number>(`${this.baseUrl}/${id}/next`, { tracker }).then(r => r.data);
    }
}

export class GlobalFunctionClient extends CrudClient<kb.IGlobalFunction> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseurl) {
        super($http, $q, baseurl);
    }

    public getDependencies(request: kb.IBatchRequest): ng.IPromise<kb.IGlobalFunction[]> {
        return this.$http.post<kb.IGlobalFunction[]>(`${this.baseUrl}/dependents`, request).then(r => r.data);
    }
}

export class UserClient extends CrudClient<kb.IKbUserEdit> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public clearProfileImage(userId: number): ng.IPromise<kb.IKbUserEdit> {
        return this.$http.delete(`/api/users/profileimage/${userId}`).then(r => r.data);
    }

    public deleteAndAnonymize(userId: number): ng.IPromise<kb.IKbUserEdit> {
        return this.$http.delete(`/api/users/anonymize/${userId}`).then(r => r.data);
    }

    public resetPassword(userId: number): ng.IPromise<kb.IKbUserEdit> {
        return this.$http.post(
            "api/users/resetpassword/" + userId, {}).then(r => r.data);
    }

    public acceptPrivacyPolicy(): ng.IPromise<kb.IKbUserEdit> {
        return this.$http.post(
            "api/users/acceptprivacypolicy", {}).then(r => r.data);
    }

    public resetDeactivation(userId: number): ng.IPromise<kb.IKbUserEdit> {
        return this.$http.put(
            "api/users/resetdeactivation/" + userId, {}).then(r => r.data);
    }
}

export class LayoutClient extends CrudClient<kb.ILayout> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public selections(tracker?: ng.IPromiseTracker): ng.IPromise<kb.ISelection[]> {
        return this.$http.get<kb.ISelection[]>(`${this.baseUrl}/selections`, { tracker }).then(r => r.data);
    }
}

export class DbWhitelistClient {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl: string) {

    }

    public add(entry:kb.IDbWhitelistEntry, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbWhitelistEntry> {
        return this.$http.post(
            this.baseUrl, entry).then(r => r.data);
    }

    public getAll(tracker?: ng.IPromiseTracker): ng.IPromise<kb.IDbWhitelistEntry[]> {
        return this.$http.get<kb.IDbWhitelistEntry[]>(this.baseUrl).then(r => r.data);
    }

    public delete(name: string, tracker?: ng.IPromiseTracker) {
        return this.$http.delete(this.baseUrl + "/" + name).then(r => r.data);
    }
}

export class DbXuserCred {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseurl: string) {

    }

    public post() {
        return this.$http.post(this.baseurl, null).then(r => r.data);
    }
}

export class KineticMomClient {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl: string) {

    }

    public get(idConfigurator:number, tracker?: ng.IPromiseTracker): ng.IPromise<any> {
        return this.$http.get(
            this.baseUrl + "/partRev/" + idConfigurator).then(r => r.data);
    }
}

export class ConfiguratorClient extends CrudClient<kb.IProduct> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public search(args?: kb.ISearchArgs, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProduct[]> {
        args = args || {};
        args.filters = args.filters || [];
        args.filters.push({
            property: meta.Product.isConfigured.name,
            values: [true]
        });
        return super.search(args, tracker);
    }

    public searchFields(id: number): ng.IPromise<{ id: string; name: string; type: string }[]> {
        return this.$http.get<{ id: string; name: string; type: string }[]>(`/api/products/${id}/searchfields`)
            .then(r => r.data);
    }

    public getIsEditing(id: number): ng.IPromise<kb.IProductUserIsEditingArgs[]> {
        return this.$http.get<any[]>(`${this.baseUrl}/${id}/isediting`).then(r => r.data);
    }

    public setIsEditing(id: number, isEditing: boolean, guid?: string): ng.IPromise<string> {
        return this.$http.post<any>(`${this.baseUrl}/${id}/isediting`, { idProduct: id, isEditing: isEditing, guid: guid }).then(r => r.data);
    }
}

export class AdminProductClient extends CrudClient<kb.IProduct> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public search(args?: kb.ISearchArgs, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProduct[]> {
        args = args || {};
        args.filters = args.filters || [];
        args.filters.push(ProductClient.getIsConfiguredFilter(false));
        return super.search(args, tracker);
    }

    public getOutput(idProduct: number, idOutput: string, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IOutputEnvelope> {
        return this.$http.get(`/api/admin/products/${idProduct}/output/${idOutput}`).then(r => r.data);
    }
}

export class ProductHistoryClient {

    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {

    }

    public getProductHistory(idProduct: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProductHistory[]> {
        return this.$http.get<kb.IProductHistory[]>(`${this.baseUrl}/${idProduct}`, { tracker }).then(r => r.data);
    }

    public getHistoryByIdHistory(idHistory: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProductHistory> {
        return this.$http.get<kb.IProductHistory>(`${this.baseUrl}/idhist/${idHistory}`, { tracker }).then(r => r.data);
    }

    public cloneHistoryToNewProduct(idProductHistory: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProduct> {
        return this.$http.post<kb.IProduct>(`${this.baseUrl}/clone/${idProductHistory}`, {}, { tracker }).then(r => r.data);
    }

    public revertProductToHistory(idProductHistory: number, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProduct> {
        return this.$http.post<kb.IProduct>(`${this.baseUrl}/revert/${idProductHistory}`, {}, { tracker }).then(r => r.data);
    }
}

export class ProductClient extends CrudClient<kb.IProduct> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public run(id: number, isTest: boolean, fast: boolean, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IConfiguratorSession> {
        let path = (fast ? `runfast` : `run?test=${isTest}`);
        return this.$http.get(`${this.baseUrl}/${id}/${path}`, { tracker }).then(r => r.data);
    }

    public autocomplete(
        productId: number,
        fieldId: string,
        query: string,
        tracker?: ng.IPromiseTracker
    ): ng.IPromise<kb.IOption[]> {
        return this.$http.get<kb.IOption[]>(`${this.baseUrl}/${productId}/autocomplete`, {
            params: { field: fieldId, query },
            tracker
        }).then(r => r.data);
    }

    public searchByCategory(args?: kb.IProductSearchArgs, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IProduct[]> {
        return this.$http.post<kb.IProduct[]>(
            "/api/products/searchByCategory",
            args,
            { tracker }
        ).then(r => r.data);
    }

    public static getIsConfiguredFilter(isConfigured: boolean): kb.IFilter {
        return {
            property: meta.Product.isConfigured.name,
            values: [isConfigured]
        };
    }

    public uniqueAttributeValues(args: kb.IUniqueAttributeValuesArgs): ng.IPromise<kb.IUniqueAttributeValue[]> {
        return this.$http.post<any[]>(`${this.baseUrl}/uniqueattributevalues`, args).then(r => r.data);
    }

    public attributeRanges(args: kb.IAttributeRangeArgs): ng.IPromise<kb.IAttributeRangeBucket[]> {
        return this.$http.post<any[]>(`${this.baseUrl}/attributeranges`, args).then(r => r.data);
    }
}

export class SafeFunctionClient extends CrudClient<kb.ISafeFunction> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public run(id: number, parameters?: {}, tracker?: ng.IPromiseTracker): ng.IPromise<any> {
        return this.$http.post(`${this.baseUrl}/run/${id}`, parameters, { tracker }).then(r => {
            if (r.status == 204) return null; //no content result should be null to be backwards compatible
            return r.data
        });
    }

    public test(id: number, args?: string, tracker?: ng.IPromiseTracker): ng.IPromise<any> {
        return this.$http.post(`${this.baseUrl}/test/${id}`, args, { tracker }).then(r => {
            if (r.status == 204) return null; //no content result should be null to be backwards compatible
            return r.data
        });
    }
}

export class QuoteClient extends CrudClient<kb.IQuote> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public save(quote: kb.IQuote, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IQuote> {
        return this.$http.post(`${this.baseUrl}/save`, quote, { tracker }).then(r => r.data);
    }

    public getProductBuildTypes(idQuote: number, tracker?: ng.IPromiseTracker): ng.IPromise<any> {
        return this.$http.get<kb.IProductBuildType[]>(`${this.baseUrl}/${idQuote}/productBuildTypes`, { tracker }).then(r => r.data);
    }

    public copyQuoteProduct(
        idQuote: number,
        idQuoteProduct: number,
        tracker?: ng.IPromiseTracker
    ): ng.IPromise<kb.IQuote> {
        return this.$http.post(`${this.baseUrl}/${idQuote}/product/${idQuoteProduct}/copy`, null, { tracker })
            .then(r => r.data);
    }

    public getQuoteProduct(
        idQuote: number,
        idQuoteProduct: number,
        tracker?: ng.IPromiseTracker
    ): ng.IPromise<kb.IQuoteProduct> {
        return this.$http.get(`${this.baseUrl}/${idQuote}/product/${idQuoteProduct}`, { tracker })
            .then(r => r.data);
    }

    public insertQuoteProduct(
        idQuote: number,
        quoteProduct: kb.IQuoteProduct,
        tracker?: ng.IPromiseTracker
    ): ng.IPromise<kb.IQuote> {
        return this.$http.post(`${this.baseUrl}/${idQuote}/product`, quoteProduct, { tracker })
            .then(r => r.data);
    }

    public editQuoteProduct(
        idQuote: number,
        quoteProduct: kb.IQuoteProduct,
        tracker?: ng.IPromiseTracker
    ): ng.IPromise<kb.IQuote> {
        return this.$http.put(`${this.baseUrl}/${idQuote}/product/${quoteProduct.id}`, quoteProduct, { tracker })
            .then(r => r.data);
    }

    public buildQuoteProduct(
        idQuote: number,
        idQuoteProduct: number,
        idBuildType: number,
        tracker?: ng.IPromiseTracker): ng.IPromise<kb.IQuote> {
        return this.$http.post(`${this.baseUrl}/${idQuote}/product/${idQuoteProduct}/build/${idBuildType}`, {}, { tracker }).then(r => r.data);
    }

    public deleteQuoteProduct(
        idQuote: number,
        quoteProduct: kb.IQuoteProduct,
        tracker?: ng.IPromiseTracker
    ): ng.IPromise<kb.IQuote> {
        return this.$http.delete(`${this.baseUrl}/${idQuote}/product/${quoteProduct.id}`, { tracker })
            .then(r => r.data);
    }

    public submit(quote: kb.IQuote, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IQuote> {
        return this.$http.post(`${this.baseUrl}/submit`, quote, { tracker }).then(r => r.data);
    }

    public runQuoteHeaderRule(quote: kb.IQuote, tracker?: ng.IPromiseTracker): ng.IPromise<kb.IQuote> {
        return this.$http.post(`${this.baseUrl}/headerrule`, quote, { tracker }).then(r => r.data);
    }
}

export class SavedSearchClient extends CrudClient<kb.ISavedSearch> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public mySearches(modelType: string): ng.IPromise<kb.ISavedSearch[]> {
        return this.$http.get<kb.ISavedSearch[]>(`${this.baseUrl}/${modelType}`).then(r => r.data);
    }
}

export class TestBuildClient extends CrudClient<kb.ITestBuild> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public getQueue(config: kb.IConfiguredProduct, buildId: string): ng.IPromise<number> {
        return this.$http.post<number>(`${this.baseUrl}/${buildId}/queue`, {
            config
        }).then(r => r.data);
    }
}

export class CompanySettingsClient extends SingletonCrudClient<kb.ICompanySetting> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public updateSfdcCredential(
        apiUsername: string,
        apiPassword: string,
        apiToken: string): ng.IPromise<any> {
        return this.$http.post(`${this.baseUrl}/sfdc-credential`, {
            sfdcApiUsername: apiUsername,
            sfdcApiPassword: apiPassword,
            sfdcApiToken: apiToken
        });
    }

    public editCurrencySetting(
        currencySetting: kb.ICurrencySetting,
        tracker?: ng.IPromiseTracker
    ): ng.IPromise<kb.ICompanySetting> {
        return this.$http.put<kb.ICompanySetting>(`${this.baseUrl}/currency`, currencySetting, { tracker })
            .then(r => r.data);
    }
}

export class QuoteHeaderClient extends CrudClient<kb.IQuoteHeader> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public getDefault(): ng.IPromise<kb.IQuoteHeader> {
        return this.$http.get(`${this.baseUrl}/default`).then(r => r.data);
    }

    public searchFields(): ng.IPromise<{ id: string; name: string; type: string }[]> {
        return this.$http.get<{ id: string; name: string; type: string }[]>(`/api/quoteheaders/searchfields`)
            .then(r => r.data);
    }
}

export class RootUserClient extends CrudClient<kb.IRootUser> {
    constructor(public $http: ng.IHttpService, public $q: ng.IQService, public baseUrl) {
        super($http, $q, baseUrl);
    }

    public isRootUser(email: string): ng.IPromise<boolean> {
        return this.$http.post<boolean>(`${this.baseUrl}/isrootuser`, { email }).then(r => r.data);
    }
}